import React, { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { I } from "./icons";

/**
 * Botón con icono "i" que abre un popover con el origen de los datos.
 * Props:
 *  - info: { titulo?, descripcion?, origen?, formula? }
 *  - placement: posición del icono dentro del host ("absolute" | "inline").
 *    "absolute" lo fija arriba a la derecha de la card padre (requiere
 *    position: relative en el padre).
 *  - size: tamaño del icono (px). Por defecto 14.
 */
const InfoBadge = ({ info, placement = "inline", size = 14 }) => {
  const { titulo = "Origen de los datos", descripcion, origen, formula } = info || {};
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const popRef = useRef(null);
  const uid = useId();

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const W = 320;
    const top = Math.min(window.innerHeight - 16, r.bottom + 8);
    const right = Math.max(8, window.innerWidth - r.right);
    setPos({ top, right, width: W });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (popRef.current?.contains(e.target)) return;
      if (btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    const onScroll = () => setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open]);

  if (!info) return null;

  const btn = (
    <button
      ref={btnRef}
      type="button"
      className={`cmi-info-btn ${placement === "absolute" ? "abs" : ""} ${open ? "active" : ""}`}
      aria-label="Información sobre los datos"
      aria-expanded={open}
      aria-controls={`infopop-${uid}`}
      title="¿De dónde vienen estos datos?"
      onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
    >
      <I.Info size={size} />
    </button>
  );

  return (
    <>
      {btn}
      {open && pos ? (
        <div
          ref={popRef}
          id={`infopop-${uid}`}
          className="cmi-info-pop"
          role="dialog"
          style={{ top: pos.top, right: pos.right, width: pos.width }}
        >
          <div className="cmi-info-pop-head">{titulo}</div>
          <div className="cmi-info-pop-body">
            {descripcion && (
              <div className="cmi-info-sec">
                <div className="cmi-info-lbl">Qué muestra</div>
                <div className="cmi-info-txt">{descripcion}</div>
              </div>
            )}
            {origen && (
              <div className="cmi-info-sec">
                <div className="cmi-info-lbl">Origen</div>
                <div className="cmi-info-txt">{origen}</div>
              </div>
            )}
            {formula && (
              <div className="cmi-info-sec">
                <div className="cmi-info-lbl">Fórmula</div>
                <div className="cmi-info-txt"><code>{formula}</code></div>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </>
  );
};

export default InfoBadge;
